'use client';

import { c as _c } from "react/compiler-runtime";
import { type GetMarketTradersRow, useActiveWallet, useAuth, useGetMarketTraders } from '@morfi/backend-sdk-v2/client';
import type { MarketPoolQuoteSnapshot } from '@morfi/market-math';
import { useTranslations } from 'next-intl';
import { useEffect, useRef, useState } from 'react';
import { useOwnRowVisibility } from '@/components/market/shared/use-own-row-visibility';
import { TabErrorBoundary } from '@/components/social/shared/tab-error-boundary';
import { InfiniteList } from '@/components/ui/infinite-list';
import { useLiveListData } from '@/hooks/sdk/use-live-list-data';
import { TRADERS_PROJECTION } from '@/lib/sdk/projections';
import { logger } from '@/lib/telemetry/logger';
import { MarketTableSortControls } from '../market-table-sort-controls';
import { MarketTradersEmpty } from './market-traders-empty';
import { MarketTradersOutcomeFilter } from './market-traders-outcome-filter';
import { MarketTradersSkeleton } from './market-traders-skeleton';
import { MarketTradersTable } from './market-traders-table';
import { MarketYourRankCard } from './market-your-rank-card';
import { type MarketTradersSort, toMarketTraderViewModel } from './trader-row';
import { useMarketTraderOwnRank } from './use-market-trader-own-rank';
interface MarketTradersTabContainerProps {
  marketPubkey: string;
  poolSnapshot: MarketPoolQuoteSnapshot | undefined;
  outcomes?: readonly OutcomeMeta[];
  /**
   * Whether to render the per-outcome filter dropdown. Default `true` (market
   * detail page). The winner detail page passes `false` (MOR-928): on a
   * resolved market, filtering to a losing outcome only ever shows negative
   * PnL, which is confusing — per-outcome analytics stay on the market recap.
   */
  showOutcomeFilter?: boolean;
  /** Only graduated winner surfaces subscribe to AMM activity. */
  subscribeAmm?: boolean;
}
interface OutcomeMeta {
  index: number;
  label: string;
  imageUrl?: string | undefined;
  imageNsfw?: boolean | undefined;
}
const EMPTY_OUTCOMES: readonly OutcomeMeta[] = [];
const TRADERS_SCROLL_SELECTOR = '[data-testid="market-traders-scroll"]';
const MARKET_TAB_MAX_HEIGHT = 560;
const MARKET_TRADERS_OUTCOME_FILTER_FOOTPRINT = 48;
export function MarketTradersTabContainer(t0) {
  const $ = _c(93);
  const {
    marketPubkey,
    poolSnapshot,
    outcomes: t1,
    showOutcomeFilter: t2,
    subscribeAmm: t3
  } = t0;
  const outcomes = t1 === undefined ? EMPTY_OUTCOMES : t1;
  const showOutcomeFilter = t2 === undefined ? true : t2;
  const subscribeAmm = t3 === undefined ? false : t3;
  const t = useTranslations("market.traders");
  const {
    isAuthenticated,
    user
  } = useAuth();
  const {
    activeWallet
  } = useActiveWallet();
  const currentUserId = user?.id ?? null;
  const [sortBy, setSortBy] = useState("total");
  const [sortOrder, setSortOrder] = useState("desc");
  const [outcomeIndex, setOutcomeIndex] = useState(null);
  const tradersRootRef = useRef(null);
  const currentPoolSnapshot = poolSnapshot?.market === marketPubkey ? poolSnapshot : undefined;
  let t4;
  if ($[0] !== currentPoolSnapshot?.outcomes) {
    t4 = currentPoolSnapshot?.outcomes.flatMap(_temp) ?? [];
    $[0] = currentPoolSnapshot?.outcomes;
    $[1] = t4;
  } else {
    t4 = $[1];
  }
  const outcomeMints = t4;
  const hasOutcomeFilter = showOutcomeFilter && outcomes.length > 1;
  let t5;
  if ($[2] !== outcomeMints || $[3] !== subscribeAmm) {
    t5 = subscribeAmm ? {
      mints: outcomeMints
    } : {};
    $[2] = outcomeMints;
    $[3] = subscribeAmm;
    $[4] = t5;
  } else {
    t5 = $[4];
  }
  let t6;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = [...TRADERS_PROJECTION];
    $[5] = t6;
  } else {
    t6 = $[5];
  }
  let t7;
  if ($[6] !== outcomeIndex) {
    t7 = outcomeIndex !== null ? {
      outcomeIndex
    } : {};
    $[6] = outcomeIndex;
    $[7] = t7;
  } else {
    t7 = $[7];
  }
  let t8;
  if ($[8] !== marketPubkey || $[9] !== sortBy || $[10] !== sortOrder || $[11] !== t5 || $[12] !== t7) {
    t8 = {
      pubkey: marketPubkey,
      sortBy,
      sortOrder,
      ...t5,
      fields: t6,
      ...t7
    };
    $[8] = marketPubkey;
    $[9] = sortBy;
    $[10] = sortOrder;
    $[11] = t5;
    $[12] = t7;
    $[13] = t8;
  } else {
    t8 = $[13];
  }
  let t9;
  if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = {
      resetPaginationOnRefetch: true
    };
    $[14] = t9;
  } else {
    t9 = $[14];
  }
  const tradersQuery = useGetMarketTraders(t8, t9);
  const liveRows = useLiveListData(tradersQuery.queryKey, tradersQuery.rows, tradersQuery.entityQueryKey, tradersQuery.projectEntityRow);
  let rankedWalletAddress;
  let t10;
  let viewModels;
  if ($[15] !== activeWallet?.address || $[16] !== currentPoolSnapshot || $[17] !== liveRows) {
    let t11;
    if ($[21] !== currentPoolSnapshot) {
      t11 = row => toMarketTraderViewModel(row, currentPoolSnapshot);
      $[21] = currentPoolSnapshot;
      $[22] = t11;
    } else {
      t11 = $[22];
    }
    viewModels = liveRows.map(t11);
    rankedWalletAddress = activeWallet?.address ?? null;
    t10 = rankedWalletAddress === null ? undefined : viewModels.find(viewModel => viewModel.trader.walletAddress === rankedWalletAddress);
    $[15] = activeWallet?.address;
    $[16] = currentPoolSnapshot;
    $[17] = liveRows;
    $[18] = rankedWalletAddress;
    $[19] = t10;
    $[20] = viewModels;
  } else {
    rankedWalletAddress = $[18];
    t10 = $[19];
    viewModels = $[20];
  }
  const ownLoadedRow = t10;
  let t11;
  if ($[23] !== outcomeMints || $[24] !== subscribeAmm) {
    t11 = subscribeAmm ? {
      outcomeMints
    } : {};
    $[23] = outcomeMints;
    $[24] = subscribeAmm;
    $[25] = t11;
  } else {
    t11 = $[25];
  }
  const t12 = isAuthenticated && ownLoadedRow === undefined;
  let t13;
  if ($[26] !== currentPoolSnapshot || $[27] !== marketPubkey || $[28] !== outcomeIndex || $[29] !== rankedWalletAddress || $[30] !== sortBy || $[31] !== sortOrder || $[32] !== t11 || $[33] !== t12) {
    t13 = {
      marketPubkey,
      sortBy,
      sortOrder,
      outcomeIndex,
      ...t11,
      rankedWalletAddress,
      currentPoolSnapshot,
      enabled: t12
    };
    $[26] = currentPoolSnapshot;
    $[27] = marketPubkey;
    $[28] = outcomeIndex;
    $[29] = rankedWalletAddress;
    $[30] = sortBy;
    $[31] = sortOrder;
    $[32] = t11;
    $[33] = t12;
    $[34] = t13;
  } else {
    t13 = $[34];
  }
  const ownRankViewModel = useMarketTraderOwnRank(t13);
  const yourRankViewModel = ownLoadedRow ?? ownRankViewModel;
  const ownRowObservationKey = ownLoadedRow === undefined ? null : `${ownLoadedRow.participationId}:${sortBy}:${sortOrder}:${String(outcomeIndex)}`;
  const ownRowVisibility = useOwnRowVisibility(tradersRootRef, ownRowObservationKey, TRADERS_SCROLL_SELECTOR, "market.traders.your_rank_observer_failed", "page-below-lg");
  const showYourRank = isAuthenticated && currentUserId !== null && yourRankViewModel !== undefined && (ownLoadedRow === undefined || ownRowVisibility.observationKey === ownRowObservationKey && !ownRowVisibility.visible);
  const isLoading = tradersQuery.isPending && viewModels.length === 0;
  let t14;
  let t15;
  if ($[35] !== marketPubkey || $[36] !== outcomeIndex || $[37] !== sortBy || $[38] !== sortOrder || $[39] !== tradersQuery.isError) {
    t14 = () => {
      if (tradersQuery.isError) {
        logger.warn("market.traders.fetch_failed", {
          marketPubkey,
          sortBy,
          sortOrder,
          outcomeScoped: outcomeIndex !== null
        });
      }
    };
    t15 = [tradersQuery.isError, marketPubkey, sortBy, sortOrder, outcomeIndex];
    $[35] = marketPubkey;
    $[36] = outcomeIndex;
    $[37] = sortBy;
    $[38] = sortOrder;
    $[39] = tradersQuery.isError;
    $[40] = t14;
    $[41] = t15;
  } else {
    t14 = $[40];
    t15 = $[41];
  }
  useEffect(t14, t15);
  const isEmpty = !tradersQuery.isPending && viewModels.length === 0;
  let t16;
  if ($[42] !== hasOutcomeFilter || $[43] !== marketPubkey || $[44] !== outcomeIndex || $[45] !== outcomes) {
    t16 = hasOutcomeFilter ? <MarketTradersOutcomeFilter marketPubkey={marketPubkey} outcomes={outcomes} selectedIndex={outcomeIndex} onSelect={idx => setOutcomeIndex(idx)} /> : null;
    $[42] = hasOutcomeFilter;
    $[43] = marketPubkey;
    $[44] = outcomeIndex;
    $[45] = outcomes;
    $[46] = t16;
  } else {
    t16 = $[46];
  }
  let t17;
  if ($[47] !== t) {
    t17 = t("volume");
    $[47] = t;
    $[48] = t17;
  } else {
    t17 = $[48];
  }
  let t18;
  if ($[49] !== t17) {
    t18 = {
      value: "volume",
      label: t17
    };
    $[49] = t17;
    $[50] = t18;
  } else {
    t18 = $[50];
  }
  let t19;
  if ($[51] !== t) {
    t19 = t("unrealized_pnl");
    $[51] = t;
    $[52] = t19;
  } else {
    t19 = $[52];
  }
  let t20;
  if ($[53] !== t19) {
    t20 = {
      value: "unrealized",
      label: t19
    };
    $[53] = t19;
    $[54] = t20;
  } else {
    t20 = $[54];
  }
  let t21;
  if ($[55] !== t) {
    t21 = t("realized_pnl");
    $[55] = t;
    $[56] = t21;
  } else {
    t21 = $[56];
  }
  let t22;
  if ($[57] !== t21) {
    t22 = {
      value: "realized",
      label: t21
    };
    $[57] = t21;
    $[58] = t22;
  } else {
    t22 = $[58];
  }
  let t23;
  if ($[59] !== t) {
    t23 = t("total_pnl");
    $[59] = t;
    $[60] = t23;
  } else {
    t23 = $[60];
  }
  let t24;
  if ($[61] !== t23) {
    t24 = {
      value: "total",
      label: t23
    };
    $[61] = t23;
    $[62] = t24;
  } else {
    t24 = $[62];
  }
  let t25;
  if ($[63] !== t18 || $[64] !== t20 || $[65] !== t22 || $[66] !== t24) {
    t25 = [t18, t20, t22, t24];
    $[63] = t18;
    $[64] = t20;
    $[65] = t22;
    $[66] = t24;
    $[67] = t25;
  } else {
    t25 = $[67];
  }
  let t26;
  if ($[68] !== sortBy || $[69] !== sortOrder || $[70] !== t25) {
    t26 = <MarketTableSortControls className="xl:hidden" sortBy={sortBy} sortOrder={sortOrder} onSortChange={setSortBy} onSortOrderChange={setSortOrder} options={t25} />;
    $[68] = sortBy;
    $[69] = sortOrder;
    $[70] = t25;
    $[71] = t26;
  } else {
    t26 = $[71];
  }
  let t27;
  if ($[72] !== currentUserId || $[73] !== hasOutcomeFilter || $[74] !== isEmpty || $[75] !== isLoading || $[76] !== marketPubkey || $[77] !== outcomeIndex || $[78] !== rankedWalletAddress || $[79] !== showYourRank || $[80] !== sortBy || $[81] !== sortOrder || $[82] !== tradersQuery.fetchNextPage || $[83] !== tradersQuery.hasNextPage || $[84] !== tradersQuery.isError || $[85] !== tradersQuery.isFetchingNextPage || $[86] !== viewModels || $[87] !== yourRankViewModel) {
    t27 = isLoading ? <MarketTradersSkeleton /> : isEmpty ? <MarketTradersEmpty /> : <div ref={tradersRootRef} className="relative isolate"><InfiniteList key={`${marketPubkey}:${sortBy}:${sortOrder}:${outcomeIndex}`} hasNextPage={tradersQuery.hasNextPage === true} isFetchingNextPage={tradersQuery.isFetchingNextPage === true} fetchNextPage={tradersQuery.fetchNextPage} isError={tradersQuery.isError} maxHeight={hasOutcomeFilter ? MARKET_TAB_MAX_HEIGHT - MARKET_TRADERS_OUTCOME_FILTER_FOOTPRINT : MARKET_TAB_MAX_HEIGHT} scrollMode="page-below-lg" {...showYourRank ? {
        className: "pb-20"
      } : {}} data-testid="market-traders-scroll"><MarketTradersTable entries={viewModels} currentWalletAddress={rankedWalletAddress} sortBy={sortBy} sortOrder={sortOrder} onSortChange={next => {
          setSortBy(next);
          setSortOrder("desc");
        }} onSortOrderChange={setSortOrder} /></InfiniteList>{yourRankViewModel !== undefined && currentUserId !== null ? <MarketYourRankCard viewModel={yourRankViewModel} userId={currentUserId} sortBy={sortBy} sortOrder={sortOrder} visible={showYourRank} /> : null}</div>;
    $[72] = currentUserId;
    $[73] = hasOutcomeFilter;
    $[74] = isEmpty;
    $[75] = isLoading;
    $[76] = marketPubkey;
    $[77] = outcomeIndex;
    $[78] = rankedWalletAddress;
    $[79] = showYourRank;
    $[80] = sortBy;
    $[81] = sortOrder;
    $[82] = tradersQuery.fetchNextPage;
    $[83] = tradersQuery.hasNextPage;
    $[84] = tradersQuery.isError;
    $[85] = tradersQuery.isFetchingNextPage;
    $[86] = viewModels;
    $[87] = yourRankViewModel;
    $[88] = t27;
  } else {
    t27 = $[88];
  }
  let t28;
  if ($[89] !== t16 || $[90] !== t26 || $[91] !== t27) {
    t28 = <TabErrorBoundary tabName="market-traders"><div className="flex flex-col gap-2">{t16}{t26}{t27}</div></TabErrorBoundary>;
    $[89] = t16;
    $[90] = t26;
    $[91] = t27;
    $[92] = t28;
  } else {
    t28 = $[92];
  }
  return t28;
}
function _temp(outcome) {
  return outcome.cpSwap === null ? [] : [outcome.outcomeMint];
}